import React from 'react';

import cs from 'classnames';
import moment from 'moment-tz';
import styled from 'styled-components';

import {UserAvatar} from 'app/components/general/avatars';
import {DocumentSequencedStepsStatusIcon} from 'app/general/doc-status';
import {StyledCheckboxButton} from 'app/procedures/components/structures';
import {gt} from 'app/utils/i18n';

import type {BasicUserSerializedType} from 'sweet-types';

interface Props {
  isCompleted: boolean;
  disabled: boolean;
  completedBy: null | BasicUserSerializedType;
  completedAt: string | null;
  userTimezone: string;
  onToggleCheck: (checked: boolean) => Promise<any>;
  isProcedureStepsBlocked: boolean;
}
export const TaskStepActionButtons = ({
  userTimezone,
  onToggleCheck,
  disabled,
  isCompleted,
  completedBy,
  completedAt,
  isProcedureStepsBlocked,
}: Props): JSX.Element => (
  <StyledStickyDiv>
    {isProcedureStepsBlocked ? (
      <DocumentSequencedStepsStatusIcon
        className="pl-1 pr-2 mr-1"
        iconClassName="fa-fw"
        contentType="procedure"
        isSequenced={true}
      />
    ) : (
      <StyledCheckboxButton
        disabled={disabled}
        isChecked={isCompleted}
        onClick={(checked) => onToggleCheck(checked)}
        fixedWidth={true}
        testid="step-checkbox"
      />
    )}
    {isCompleted && completedAt != null ? (
      <div className="ml-2">
        <UserAvatar
          user={completedBy ?? undefined}
          small={true}
          showCard
          tooltipTitle={(userName) =>
            getCompletionDetails(completedAt, userName, userTimezone)
          }
        />
      </div>
    ) : null}
  </StyledStickyDiv>
);

export const StyledStickyDiv = styled.div.attrs<{
  noPadding?: boolean;
}>(({noPadding}) => ({
  className: cs(
    'float-right d-flex flex-row-reverse align-items-center position-sticky',
    {'py-1': !noPadding}
  ),
}))<{
  noPadding?: boolean;
}>`
  top: 10px;
  z-index: 4;
  border-radius: 4px 0 0 4px;
  background-color: rgba(255, 255, 255, 0.95);
  // fix Firefox sticky bug
  // https://github.com/SweetProcess/sweetprocess.com/issues/6580#issuecomment-718057462
  height: 100%;
`;

const getCompletionDetails = (
  completedAt: string,
  userName: React.ReactNode,
  tz: string
): React.ReactNode =>
  gt(
    'Step completed by %s on %s',
    <b>{userName}</b>,
    moment(completedAt).tz(tz).format('lll')
  );
